import { Divider } from '@advanza/ui'
import Card from 'components/Card'
import {
    formatNativeAppDate,
    formatNativeAppMinutes,
    formatNativeAppRelative,
    NativeAppDevice,
    useProviderNativeAppStats,
} from 'hooks/providerNativeAppHooks'
import { ReactNode } from 'react'

interface ProviderNativeAppCardProps {
    providerId: number | string
    mobileAppInstalled: boolean
}

const NOT_AVAILABLE = '–'

interface RowProps {
    label: string
    children: ReactNode
}

const Row = ({ label, children }: RowProps) => (
    <tr>
        <td className="py-1 pr-4 text-neutral-500 align-top">{label}</td>
        <td className="py-1 font-medium">{children}</td>
    </tr>
)

interface PlaceholderProps {
    children?: ReactNode
}

const Placeholder = ({ children = NOT_AVAILABLE }: PlaceholderProps) => (
    <span className="text-neutral-400 italic">{children}</span>
)

const LoadingCell = () => <Placeholder>Loading…</Placeholder>

interface DeviceBadgeProps {
    device: NativeAppDevice
}

const DeviceBadge = ({ device }: DeviceBadgeProps) => (
    <span className="inline-flex items-center gap-1.5">
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-sm font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200/50 uppercase">
            {device.platform}
        </span>
        {device.device_name && (
            <span className="text-sm text-neutral-500 font-normal">{device.device_name}</span>
        )}
    </span>
)

interface DormantBadgeProps {
    daysInactive: number | null
}

const DormantBadge = ({ daysInactive }: DormantBadgeProps) => (
    <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-sm font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"
        title="No app activity in over 30 days — matches the Dormant cohort in the native app analytics.">
        Dormant{daysInactive === null ? '' : ` · ${daysInactive}d`}
    </span>
)

interface PausedBadgeProps {
    daysInactive: number | null
    pauseReason: string | null
    pausedUntil: string | null
}

const buildPauseTooltip = (pauseReason: string | null, pausedUntil: string | null): string => {
    const base = pauseReason ? `Paused (${pauseReason})` : 'Paused'
    const until = pausedUntil ? ` until ${formatNativeAppDate(pausedUntil)}` : ''
    return `${base}${until} — inactivity is expected, so this provider is not counted as dormant.`
}

const PausedBadge = ({ daysInactive, pauseReason, pausedUntil }: PausedBadgeProps) => (
    <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-sm font-semibold bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200"
        title={buildPauseTooltip(pauseReason, pausedUntil)}>
        Paused{daysInactive === null ? '' : ` · ${daysInactive}d`}
    </span>
)

const pickEarliestDevice = (devices: NativeAppDevice[] | undefined): NativeAppDevice | null => {
    if (!devices || devices.length === 0) return null
    return devices.reduce((earliest, d) => {
        if (!earliest.registered_at) return d
        if (!d.registered_at) return earliest
        return new Date(d.registered_at) < new Date(earliest.registered_at) ? d : earliest
    })
}

const ProviderNativeAppCard = ({ providerId, mobileAppInstalled }: ProviderNativeAppCardProps) => {
    const { loading, report } = useProviderNativeAppStats(providerId)
    const isLoading = loading && !report

    const devices = report?.devices
    const primaryDevice = pickEarliestDevice(devices)
    const extraDeviceCount = devices && devices.length > 1 ? devices.length - 1 : 0
    const isDormant = report?.dormant ?? false
    const isPaused = report?.paused ?? false

    const renderAppInstalled = (): ReactNode => {
        const text = mobileAppInstalled ? 'Yes' : 'No'
        if (!mobileAppInstalled || !primaryDevice) return text
        return (
            <span className="inline-flex items-center gap-2 flex-wrap">
                <span>{text}</span>
                <DeviceBadge device={primaryDevice} />
                {extraDeviceCount > 0 && (
                    <span className="text-sm text-neutral-500 font-normal">
                        +{extraDeviceCount} more
                    </span>
                )}
            </span>
        )
    }

    const renderInstalledSince = (): ReactNode => {
        if (isLoading) return <LoadingCell />
        const registered = primaryDevice?.registered_at
        if (!registered) return <Placeholder />
        const relative = formatNativeAppRelative(registered)
        return (
            <span>
                {formatNativeAppDate(registered)}
                {relative && (
                    <span className="text-sm text-neutral-400 font-normal ml-1">({relative})</span>
                )}
            </span>
        )
    }

    const renderLastActive = (): ReactNode => {
        if (isLoading) return <LoadingCell />
        const lastActive = report?.activity?.last_active
        if (!lastActive) return <Placeholder />
        const relative = formatNativeAppRelative(lastActive)
        return (
            <span>
                {formatNativeAppDate(lastActive, true)}
                {relative && (
                    <span className="text-sm text-neutral-400 font-normal ml-1">({relative})</span>
                )}
            </span>
        )
    }

    const renderPushEnabled = (): ReactNode => {
        if (isLoading) return <LoadingCell />
        const enabled = report?.push_notifications_enabled ?? false
        const sent = report?.notifications?.sent ?? 0
        const openRate = report?.notifications?.open_rate ?? 0
        if (!enabled) return 'No'
        if (sent === 0) {
            return (
                <span className="inline-flex items-center gap-2 flex-wrap">
                    <span>Yes</span>
                    <span className="text-sm text-neutral-400 font-normal">no pushes sent yet</span>
                </span>
            )
        }
        return (
            <span className="inline-flex items-center gap-2 flex-wrap">
                <span>Yes</span>
                <span className="text-sm text-neutral-500 font-normal">
                    {sent} sent · {openRate}% opened
                </span>
            </span>
        )
    }

    const renderAvgResponse = (): ReactNode => {
        if (isLoading) return <LoadingCell />
        const minutes = report?.response_time?.avg_response_time_minutes ?? null
        if (minutes === null) return <Placeholder />
        return formatNativeAppMinutes(minutes)
    }

    // Only show a status badge for quiet providers. A provider on a lead pause is expected to be
    // quiet, so they get a "Paused" badge instead of the alarming "Dormant" one.
    const renderStatusBadge = (): ReactNode => {
        if (!isDormant) return undefined
        const daysInactive = report?.days_inactive ?? null
        if (isPaused) {
            return (
                <PausedBadge
                    daysInactive={daysInactive}
                    pauseReason={report?.pause_reason ?? null}
                    pausedUntil={report?.paused_until ?? null}
                />
            )
        }
        return <DormantBadge daysInactive={daysInactive} />
    }

    return (
        <Card icon="phone_android" header="Native app" topRight={renderStatusBadge()}>
            <Divider />
            <table className="w-full text-md">
                <tbody>
                    <Row label="App installed">{renderAppInstalled()}</Row>
                    <Row label="Installed since">{renderInstalledSince()}</Row>
                    <Row label="Last app login">{renderLastActive()}</Row>
                    <Row label="Push notifications">{renderPushEnabled()}</Row>
                    <Row label="Avg response to lead">{renderAvgResponse()}</Row>
                </tbody>
            </table>
        </Card>
    )
}

export default ProviderNativeAppCard
